import { useParams, useNavigate } from 'react-router-dom'
import { Home, Car, Briefcase, MapPin, Calendar, Phone, Heart, Share2, ChevronLeft, CheckCircle, Shield, Eye, MessageSquare, X, FileText, GraduationCap, Users, Globe, Building, Armchair, Cpu, Shirt, MoreHorizontal, Edit, Trash2 } from 'lucide-react'
import { useEffect, useState, useRef } from 'react'
import useSound from '../hooks/useSound'
import { listingService } from '../lib/listingService'
import { messageService } from '../lib/messageService'
import { useAuth } from '../contexts/AuthContext'
import ReportButton from '../components/ReportButton'
import OptimizedImage from '../components/OptimizedImage'
import type { Listing, PropertyListing } from '../types/listing'

const ListingDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [listing, setListing] = useState<Listing | null>(null)
  const [loading, setLoading] = useState(true)
  const [similarListings, setSimilarListings] = useState<Listing[]>([])
  const { user, isAdmin, isModerator } = useAuth()
  const [deleting, setDeleting] = useState(false)
  const playClick = useSound('click')
  const playNavigation = useSound('navigation')
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null)
  const hasIncrementedRef = useRef<string | null>(null)

  useEffect(() => {
    const loadListing = async () => {
      if (!id) return
      setLoading(true)
      try {
        // In a real app, we would fetch by ID from Firebase
        // For now, use mock data from listingService
         const allListings = await listingService.getListings({})
         const found = allListings.find(l => l.id === id)
         if (found) {
           setListing(found)
           
           // Increment view count only once per session
           if (hasIncrementedRef.current !== id) {
             const sessionKey = `viewed_listing_${id}`
             const hasViewed = sessionStorage.getItem(sessionKey)
             
             if (!hasViewed) {
               console.log(`Incrementing views for listing ${id} (first view this session)`)
               try {
                 await listingService.incrementViews(id)
                 // Update local state with incremented views
                 setListing(prev => prev ? { ...prev, views: prev.views + 1 } : null)
                 sessionStorage.setItem(sessionKey, 'true')
               } catch (error) {
                 console.error('Failed to increment views:', error)
               }
               hasIncrementedRef.current = id
             } else {
               console.log(`Listing ${id} already viewed this session, skipping view increment`)
               hasIncrementedRef.current = id
             }
           }
           
           // Find similar listings of same type
           const similar = allListings
             .filter(l => l.type === found.type && l.id !== found.id)
             .slice(0, 3)
           setSimilarListings(similar)
        } else {
          // If not found, navigate to listings page
          navigate('/listings')
        }
      } catch (error) {
        console.error('Failed to load listing:', error)
      } finally {
        setLoading(false)
      }
    }
    loadListing()
  }, [id, navigate])

  const handleStartChat = async () => {
    playClick()
    if (!user) {
      if (window.confirm('Você precisa estar logado para enviar uma mensagem. Deseja fazer login agora?')) {
        navigate('/login')
      }
      return
    }
    if (!listing) return
    // Prevent chatting with yourself
    if (user.uid === listing.userId) {
      alert('Você não pode enviar mensagem para seu próprio anúncio.')
      return
    }
    try {
      const conversationId = await messageService.findOrCreateListingConversation(
        user.uid,
        listing.userId,
        listing.id,
        listing.title,
        listing.price,
        listing.images[0]
      )
      navigate(`/messages/${conversationId}`)
    } catch (error) {
      console.error('Failed to start chat:', error)
      alert('Erro ao iniciar conversa. Tente novamente.')
    }
  }

  const handleAvatarClick = () => {
    if (!listing) return
    navigate(`/profile/${listing.userId}`)
  }

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index)
  }

  const closeLightbox = () => {
    setSelectedImageIndex(null)
  }

  const handlePrevImage = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation()
    if (selectedImageIndex === null || !listing) return
    const prevIndex = selectedImageIndex > 0 ? selectedImageIndex - 1 : listing.images.length - 1
    setSelectedImageIndex(prevIndex)
  }

  const handleNextImage = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation()
    if (selectedImageIndex === null || !listing) return
    const nextIndex = selectedImageIndex < listing.images.length - 1 ? selectedImageIndex + 1 : 0
    setSelectedImageIndex(nextIndex)
  }

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedImageIndex === null || !listing) return
      
      if (e.key === 'Escape') {
        closeLightbox()
      } else if (e.key === 'ArrowLeft') {
        handlePrevImage()
      } else if (e.key === 'ArrowRight') {
        handleNextImage()
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [selectedImageIndex, listing])

  const formatPrice = (price: number, unit?: string) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: unit === 'BRL' ? 'BRL' : 'USD',
    }).format(price)
  }

  const getTypeIcon = (type: string) => {
    const mainCategory = listing?.mainCategory
    if (type === 'generic' && mainCategory) {
      switch (mainCategory) {
        case 'housing': return Home
        case 'cars': return Car
        case 'services': return Briefcase
        case 'documents': return FileText
        case 'jobs': return Briefcase
        case 'education': return GraduationCap
        case 'community': return Users
        case 'saude': return Heart
        case 'tecnologia': return Globe
        case 'negocios': return Building
        case 'furniture': return Armchair
        case 'electronics': return Cpu
        case 'fashion': return Shirt
        case 'others': return MoreHorizontal
        default: return Home
      }
    }
    switch (type) {
      case 'property': return Home
      case 'car': return Car
      case 'service': return Briefcase
      default: return Home
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'property': return 'Imóvel'
      case 'car': return 'Carro'
      case 'service': return 'Serviço'
      case 'generic': {
        // Map mainCategory to label
        const mainCategory = listing?.mainCategory
        if (mainCategory) {
          const categoryLabels: Record<string, string> = {
            housing: 'Imóvel',
            cars: 'Carro',
            services: 'Serviço',
            documents: 'Documentos',
            jobs: 'Empregos',
            education: 'Educação',
            community: 'Comunidade',
            saude: 'Saúde',
            tecnologia: 'Tecnologia',
            negocios: 'Negócios',
            furniture: 'Móveis',
            electronics: 'Eletrônicos',
            fashion: 'Moda',
            others: 'Outros',
          }
          return categoryLabels[mainCategory] || mainCategory
        }
        return 'Outros'
      }
      default: return type
    }
  }

  const TypeIcon = listing ? getTypeIcon(listing.type) : Home
  const isProperty = listing?.type === 'property'
  const isCar = listing?.type === 'car'
  const isService = listing?.type === 'service'

  const handleDelete = async () => {
    if (!listing) return
    if (!window.confirm('Tem certeza que deseja excluir este anúncio?')) return
    setDeleting(true)
    try {
      await listingService.deleteListing(listing.id, user?.uid)
      navigate('/listings')
    } catch (error) {
      console.error('Error deleting listing:', error)
      alert('Não foi possível excluir o anúncio.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 flex-grow">
      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-noe-brand"></div>
        </div>
      ) : !listing ? (
        <div className="text-center">
          <h1 className="text-2xl font-bold">Listagem não encontrada</h1>
          <button
            onClick={() => navigate('/listings')}
            className="mt-4 px-4 py-2 bg-noe-brand text-white rounded-lg hover:bg-noe-brand/80 transition-colors"
          >
            Voltar ao Marketplace
          </button>
        </div>
      ) : (<>
        {/* Back button */}
       <button
         onClick={() => { playNavigation(); navigate('/listings'); }}
         className="flex items-center gap-2 text-noe-400 hover:text-noe-brand transition-colors mb-6"
       >
         <ChevronLeft className="w-5 h-5" />
         Voltar para listagens
       </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left column: Main content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Header */}
          <div>
            <div className="flex items-center gap-4 mb-4">
              <div className={`p-3 rounded-xl ${
                listing.type === 'property' ? 'bg-emerald-500/20 text-emerald-400' :
                listing.type === 'car' ? 'bg-blue-500/20 text-blue-400' :
                'bg-amber-500/20 text-amber-400'
              }`}>
                <TypeIcon className="w-6 h-6" />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-bold">{listing.title}</h1>
                  <span className="px-3 py-1 bg-noe-brand/20 text-noe-brand rounded-full text-sm font-medium">
                    {getTypeLabel(listing.type)}
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-2 text-noe-500">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    <span>{listing.location}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>Publicado em {new Date(listing.createdAt).toLocaleDateString('pt-BR')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    <span>{listing.views} visualizações</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="bg-noe-900/30 border border-noe-800 rounded-2xl p-4 backdrop-blur-sm">
            {/* Action buttons */}
            <div className="flex items-center justify-end gap-4 mb-4">
              <button className="flex items-center gap-2 text-noe-400 hover:text-noe-brand transition-colors">
                <Heart className="w-5 h-5" />
                Favoritar
              </button>
              <button className="flex items-center gap-2 text-noe-400 hover:text-noe-brand transition-colors">
                <Share2 className="w-5 h-5" />
                Compartilhar
              </button>
              <ReportButton
                targetType="listing"
                targetId={listing.id}
                targetTitle={listing.title}
                targetUserId={listing.userId}
                size="sm"
              />
              {(isAdmin || isModerator) && (
                <>
                  <button
                     onClick={() => navigate(`/listings/${listing.id}/edit`)}
                    className="flex items-center gap-2 text-noe-400 hover:text-noe-brand transition-colors"
                    title="Editar anúncio"
                  >
                    <Edit className="w-5 h-5" />
                    Editar
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="flex items-center gap-2 text-noe-400 hover:text-red-400 transition-colors disabled:opacity-50"
                    title="Excluir anúncio"
                  >
                    <Trash2 className="w-5 h-5" />
                    {deleting ? 'Excluindo...' : 'Excluir'}
                  </button>
                </>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               {listing.images.length > 0 ? (
                 <>
                    {/* First image - large */}
                    <div className="md:col-span-2" onClick={() => handleImageClick(0)}>
                      <div className="aspect-video rounded-xl overflow-hidden bg-noe-800/50">
                        <OptimizedImage
                          src={listing.images[0]}
                          alt={listing.title}
                          className="w-full h-full object-cover cursor-pointer"
                          width={800}
                          height={450}
                          fallbackSrc={`https://via.placeholder.com/800x450/bfa757/ffffff?text=${encodeURIComponent(listing.title)}`}
                        />
                      </div>
                    </div>
                   
                   {/* Second and third images (or remaining) */}
                   {listing.images.slice(1, 3).map((image, index) => {
                     const imageIndex = index + 1
                     const isThirdWithMore = imageIndex === 2 && listing.images.length > 3
                     return (
                       <div key={imageIndex} className="relative aspect-square rounded-lg overflow-hidden bg-noe-800/30 border border-noe-800" onClick={() => handleImageClick(imageIndex)}>
                         <OptimizedImage
                           src={image}
                           alt={`${listing.title} - ${imageIndex + 1}`}
                           className="w-full h-full object-cover cursor-pointer"
                           width={400}
                           height={400}
                           fallbackSrc={`https://via.placeholder.com/400x400/bfa757/ffffff?text=${encodeURIComponent(listing.title)}`}
                         />
                         {isThirdWithMore && (
                           <div className="absolute inset-0 bg-black/70 flex items-center justify-center cursor-pointer">
                             <div className="text-white text-2xl font-bold">
                               +{listing.images.length - 3}
                             </div>
                           </div>
                         )}
                       </div>
                     )
                   })}
                   
                   {/* If only 2 images, show empty placeholder for third slot */}
                   {listing.images.length === 2 && (
                     <div className="aspect-square rounded-lg bg-noe-800/30 border border-noe-800 flex items-center justify-center">
                       <span className="text-noe-500">Sem imagem adicional</span>
                     </div>
                   )}
                 </>
               ) : (
                <>
                  <div className="md:col-span-2">
                    <div className="aspect-video rounded-xl bg-noe-800/50 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-6xl mb-4">
                          {listing.type === 'property' ? '🏠' : listing.type === 'car' ? '🚗' : '🧰'}
                        </div>
                        <p className="text-noe-400">Imagem principal da listagem</p>
                      </div>
                    </div>
                  </div>
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="aspect-square rounded-lg bg-noe-800/30 border border-noe-800 flex items-center justify-center">
                      <span className="text-noe-500">Imagem {i}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>

          {/* Lightbox Modal */}
          {selectedImageIndex !== null && listing && (
            <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={closeLightbox}>
              <div className="relative max-w-5xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
                {/* Close button */}
                <button
                  onClick={closeLightbox}
                  className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-2"
                >
                  <X className="w-6 h-6" />
                </button>
                
                {/* Navigation buttons */}
                {listing.images.length > 1 && (
                  <>
                    <button
                      onClick={handlePrevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-3"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={handleNextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-3"
                    >
                      <ChevronLeft className="w-6 h-6 transform rotate-180" />
                    </button>
                  </>
                )}
                
                {/* Image container */}
                <div className="rounded-lg overflow-hidden">
                  <OptimizedImage
                    src={listing.images[selectedImageIndex]}
                    alt={`${listing.title} - ${selectedImageIndex + 1}`}
                    className="w-full h-auto max-h-[80vh] object-contain"
                    width={1200}
                    height={800}
                    fallbackSrc={`https://via.placeholder.com/1200x800/bfa757/ffffff?text=${encodeURIComponent(listing.title)}`}
                  />
                </div>
                
                {/* Image counter */}
                <div className="text-white text-center mt-4">
                  {selectedImageIndex + 1} / {listing.images.length}
                </div>
              </div>
            </div>
          )}

          {/* Details */}
          <div className="bg-noe-900/30 border border-noe-800 rounded-2xl p-6 backdrop-blur-sm">
            <h2 className="text-xl font-bold mb-6">Detalhes</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Descrição</h3>
                  <p className="text-noe-300">{listing.description}</p>
                </div>
                {isProperty && 'bedrooms' in listing && (
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Características do Imóvel</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-noe-500">Quartos:</span>
                        <span className="font-medium">{listing.bedrooms}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-noe-500">Banheiros:</span>
                        <span className="font-medium">{listing.bathrooms}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-noe-500">Área:</span>
                        <span className="font-medium">{listing.area} m²</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-noe-500">Mobiliado:</span>
                        <span className="font-medium">{(listing as PropertyListing).amenities?.includes('mobiliado') ? 'Sim' : 'Não'}</span>
                      </div>
                    </div>
                  </div>
                )}
                {isCar && 'year' in listing && (
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Especificações do Carro</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-noe-500">Ano:</span>
                        <span className="font-medium">{listing.year}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-noe-500">Marca:</span>
                         <span className="font-medium">{listing.make}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-noe-500">Modelo:</span>
                        <span className="font-medium">{listing.model}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-noe-500">Quilometragem:</span>
                        <span className="font-medium">{listing.mileage.toLocaleString('pt-BR')} km</span>
                      </div>
                    </div>
                  </div>
                )}
                {isService && 'category' in listing && (
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Informações do Serviço</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-noe-500">Categoria:</span>
                        <span className="font-medium">{listing.category}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-noe-500">Experiência:</span>
                         <span className="font-medium">{listing.providerExperience} anos</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-noe-500">Disponibilidade:</span>
                        <span className="font-medium">{listing.availability}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-6">
                {/* Price card */}
                <div className="bg-noe-800/30 border border-noe-700 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                       <div className="text-2xl font-bold">{formatPrice(listing.price, listing.priceUnit)}</div>
                      <div className="text-noe-500 text-sm">
                        {listing.type === 'property' && 'por mês'}
                        {listing.type === 'car' && 'à vista'}
                        {listing.type === 'service' && listing.priceUnit}
                      </div>
                    </div>
                    <div className="text-emerald-400">
                      <CheckCircle className="w-6 h-6" />
                    </div>
                  </div>
                    <button 
                      onClick={handleStartChat}
                      className="w-full py-3 bg-noe-brand text-white rounded-lg font-medium hover:bg-noe-brand/80 transition-colors flex items-center justify-center gap-2"
                    >
                      <MessageSquare className="w-5 h-5" />
                      Enviar mensagem
                    </button>

                </div>

                 {/* Seller info */}
                 <div className="bg-noe-800/30 border border-noe-700 rounded-xl p-6">
                   <h3 className="text-lg font-semibold mb-4">Anunciante</h3>
                   <div className="flex items-center gap-4 mb-4">
                      {listing.userAvatar ? (
                        <button
                          onClick={handleAvatarClick}
                          className="w-12 h-12 rounded-full overflow-hidden bg-noe-brand/20 focus:outline-none focus:ring-2 focus:ring-noe-brand"
                        >
                          <OptimizedImage
                            src={listing.userAvatar}
                            alt={listing.userName}
                            className="w-full h-full object-cover"
                            width={48}
                            height={48}
                            fallbackSrc={`https://ui-avatars.com/api/?name=${encodeURIComponent(listing.userName)}&background=bfa757&color=000000`}
                          />
                        </button>
                      ) : (
                        <button
                          onClick={handleAvatarClick}
                          className="w-12 h-12 rounded-full bg-noe-brand/20 flex items-center justify-center text-noe-brand font-bold text-xl focus:outline-none focus:ring-2 focus:ring-noe-brand"
                        >
                          {listing.userName.charAt(0)}
                        </button>
                      )}
                     <div>
                       <div className="font-medium">{listing.userName}</div>
                       <div className="text-sm text-noe-500">Membro desde 2023</div>
                     </div>
                   </div>
                   <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-noe-500" />
                      <span className="text-noe-300">{listing.contactPhone}</span>
                    </div>
                     <div className="flex items-center gap-3">
                      <MessageSquare className="w-4 h-4 text-noe-500" />
                      <span className="text-noe-300">{listing.contactWhatsApp}</span>
                    </div>
                   </div>
                   <button
                     onClick={handleStartChat}
                     className="w-full mt-4 py-2.5 bg-noe-brand/20 text-noe-brand rounded-lg font-medium hover:bg-noe-brand/30 transition-colors flex items-center justify-center gap-2"
                   >
                     <MessageSquare className="w-4 h-4" />
                     Enviar mensagem
                   </button>
                   <div className="flex items-center gap-2 mt-4 text-sm text-noe-500">
                     <Shield className="w-4 h-4" />
                     <span>Verificado pelo Grupo Noé</span>
                   </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right column: Similar listings */}
        <div className="space-y-6">
          <div className="bg-noe-900/30 border border-noe-800 rounded-2xl p-6 backdrop-blur-sm">
            <h2 className="text-xl font-bold mb-6">Listagens Similares</h2>
            <div className="space-y-4">
              {similarListings.length > 0 ? (
                similarListings.map((similar) => {
                  const Icon = getTypeIcon(similar.type);
                  return (
                    <div
                      key={similar.id}
                      className="p-4 bg-noe-800/30 rounded-xl border border-noe-800 hover:border-noe-brand/30 transition-all cursor-pointer"
                      onClick={() => navigate(`/listings/${similar.id}`)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${
                          similar.type === 'property' ? 'bg-emerald-500/20 text-emerald-400' :
                          similar.type === 'car' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-amber-500/20 text-amber-400'
                        }`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="font-medium">{similar.title}</div>
                          <div className="text-sm text-noe-500">{similar.location}</div>
                          <div className="text-noe-brand font-bold mt-2">
                            {formatPrice(similar.price, similar.priceUnit)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center text-noe-500 py-4">
                  Nenhuma listagem similar encontrada
                </div>
              )}
            </div>
          </div>

          {/* Safety tips */}
          <div className="bg-noe-900/30 border border-noe-800 rounded-2xl p-6 backdrop-blur-sm">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-noe-brand" />
              Dicas de Segurança
            </h2>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-noe-brand mt-1.5"></div>
                <span>Combine encontros em locais públicos e seguros</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-noe-brand mt-1.5"></div>
                <span>Verifique a identidade do anunciante</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-noe-brand mt-1.5"></div>
                <span>Desconfie de preços muito abaixo do mercado</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-noe-brand mt-1.5"></div>
                <span>Nunca transfira dinheiro antes de verificar o produto</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </>
      )
    }
    </div>
  )
}



export default ListingDetailPage